// Mirrors the api's ContentType (api/src/chat/IContentEmbedQueueMessage.ts). Kept in sync by hand — the
// two repos don't share a types package for this.
export type InsightRefContentType = 'transcript' | 'note' | 'appointment' | 'task' | 'memory';

// Mirrors the api's NoteKind (api/src/chat/IContentEmbedQueueMessage.ts). Only set when contentType is 'note'.
export type InsightRefNoteKind = 'care-note' | 'comment' | 'checkin-personal' | 'checkin-care';

export interface IInsightRef {
    contentType: InsightRefContentType;
    sourceId: string;
    noteKind?: InsightRefNoteKind;
    authorId?: string;
}

// A model-visible reference to one piece of evidence backing an insight, e.g. "note:abc123:kind=care-note".
// The api (api/src/chat/contentAddress.ts) is the SINGLE owner of this format —
// `contentType:sourceId[:k=v...]`, where k is one of kind/by/parent/source/scope/appt — and is the only
// place that builds these refs; IInsightEvidence.ref carries the format's bare (unbracketed) form.
//
// The app only ever needs enough of a ref to know WHAT an evidence item is and route to it, so this
// parses just contentType, sourceId, the note sub-kind (kind=), and the author (by= — personal
// check-ins are author-only, so routing needs it to gate tappability), ignoring the remaining
// segments (parent/source/scope/appt), which only matter for the api's own record lookup.
export const parseInsightRef = (raw: string): IInsightRef | null => {
    const parts = raw.split(':');
    if (parts.length < 2 || !parts[0] || !parts[1]) {
        return null;
    }

    const [contentType, sourceId, ...rest] = parts;

    // narrowing a wire string to our union types requires a cast — mirrors the api's own
    // parseContentRef, which does the same for the same reason (untrusted string input)
    const ref: IInsightRef = { contentType: contentType as InsightRefContentType, sourceId };

    for (const kv of rest) {
        const eq = kv.indexOf('=');
        if (eq < 0) {
            continue;
        }
        const key = kv.slice(0, eq);
        if (key === 'kind') {
            ref.noteKind = kv.slice(eq + 1) as InsightRefNoteKind;
        } else if (key === 'by') {
            ref.authorId = kv.slice(eq + 1);
        }
    }

    return ref;
};
